import { create } from "zustand";
import { sendEmail } from "../services/emailService";

const VERIFICATION_TTL_MS = 30 * 60 * 1000;
const SESSION_KEY = "acquireos_session";

// ── Role definitions ─────────────────────────────────────────────────────────
export const ROLES = {
  HOST:   'host',
  ADMIN:  'admin',
  DEVELOPER: 'developer',
  TEAM_MEMBER: 'team_member',
  INVESTOR: 'investor', // legacy default
}

export const ROLE_PERMISSIONS = {
  host:        ['billing', 'white_label', 'all_data', 'admin', 'developer_console', 'user_management', 'lender_management', 'checklist_upload', 'settings', 'deals'],
  admin:       ['admin', 'user_management', 'lender_management', 'checklist_upload', 'settings', 'deals'],
  developer:   ['developer_console', 'api_keys', 'webhook_logs', 'system_config'],
  team_member: ['deals'],
  investor:    ['deals'],
}

export function hasPermission(user, permission) {
  if (!user?.role) return false;
  const perms = ROLE_PERMISSIONS[user.role] || [];
  return perms.includes(permission);
}

export function isAdmin(user) {
  return ['host', 'admin'].includes(user?.role);
}

export function isDeveloper(user) {
  return ['host', 'developer'].includes(user?.role);
}

// Save session to localStorage
const saveSession = (user) => {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ user, savedAt: Date.now() }));
  } catch (e) {
    console.warn("Could not save session:", e);
  }
};

// Load session from localStorage
const loadSession = () => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

// Clear session from localStorage
const clearSession = () => {
  try {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem('aq_user_role');
  } catch (e) {}
};

const useAuthStore = create((set, get) => ({
  // Authentication state
  isAuthenticated: false,
  user: null,
  isLoading: false,
  isVerifying: false,
  verificationCode: null,
  verificationTTL: VERIFICATION_TTL_MS,
  verificationSentAt: null,
  lastEmailPreview: null,
  tempUser: null,
  tempRegistration: null,
  isVerifyingRegister: false,

  generateVerificationCode: () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  },

  sendVerificationEmail: async (email, code, userName) => {
    return await sendEmail(email, code, userName);
  },

  loginWithEmail: async ({ email }) => {
    set({ isLoading: true });

    try {
      await new Promise(resolve => setTimeout(resolve, 800));

      if (!email) {
        throw new Error('Email address is required');
      }

      // Check user registration status in database
      const isSuperAdmin = email.toLowerCase() === 'admin@acquireos.com';
      if (!isSuperAdmin) {
        let userStatus = null;
        let userExists = false;

        try {
          const checkRes = await fetch(`/api/admin/users/check?email=${encodeURIComponent(email)}`);
          if (checkRes.ok) {
            const checkData = await checkRes.json();
            userExists = checkData.exists;
            userStatus = checkData.status;
          }
        } catch (_) {
          // Server unreachable — fall back to local registrations
        }

        let isLocalOnly = false;
        if (!userExists) {
          const localUsers = JSON.parse(localStorage.getItem('acquireos_local_users') || '[]');
          const localUser = localUsers.find(u => u.email.toLowerCase() === email.toLowerCase());
          if (localUser) {
            userExists = true;
            userStatus = 'Active'; // local-only users bypass admin approval
            isLocalOnly = true;
            // Upgrade any Pending local entry to Active
            localUser.status = 'Active';
            localStorage.setItem('acquireos_local_users', JSON.stringify(localUsers));
          }
        }

        if (!userExists) {
          throw new Error('This email is not registered. Please sign up first.');
        }
        if (!isLocalOnly) {
          if (userStatus === 'Pending') {
            throw new Error('Your registration is pending administrator review. You will receive an email once approved.');
          }
          if (userStatus === 'Rejected') {
            throw new Error('Your registration request has been rejected by the administrator.');
          }
          if (userStatus === 'Suspended') {
            throw new Error('Your account access has been suspended. Please contact support.');
          }
        }
      }

      const tempUser = {
        id: 'email_' + Date.now(),
        name: email.split('@')[0],
        email,
        role: isSuperAdmin ? 'admin' : 'investor',
        subscription: 'pro',
        authProvider: 'email'
      };

      const verificationCode = get().generateVerificationCode();

      const emailResult = await get().sendVerificationEmail(email, verificationCode, tempUser.name);

      if (!emailResult.success) {
        throw new Error(emailResult.error || 'Failed to send verification email');
      }

      // Log OTP to console only (not shown in UI)
      if (import.meta.env.DEV) {
        console.info(`%c[AcquireOS Dev] OTP Code for ${email}: ${verificationCode}`, 'background:#1e3a8a;color:#fff;padding:4px 10px;border-radius:4px;font-size:14px;font-weight:bold;');
      }

      set({
        tempUser,
        verificationCode,
        verificationSentAt: Date.now(),
        lastEmailPreview: null,
        isLoading: false,
        isVerifying: true
      });

      return { success: true, requiresVerification: true };
    } catch (error) {
      set({ isLoading: false });
      return { success: false, error: error.message };
    }
  },

  registerUser: async ({
    name,
    email,
    company,
    role = 'investor',
    userType = 'individual',
    orgName = null,
    orgUrl = null,
    orgDetails = null,
    indStrategy = null,
    indDealSize = null,
    indAccredited = null
  }) => {
    set({ isLoading: true });
    try {
      if (!email) {
        throw new Error('Email address is required');
      }

      // Check if user already exists (server first, then local fallback)
      try {
        const checkRes = await fetch(`/api/admin/users/check?email=${encodeURIComponent(email)}`);
        if (checkRes.ok) {
          const checkData = await checkRes.json();
          if (checkData.exists && checkData.status !== 'Rejected') {
            throw new Error('This email is already registered. Please sign in instead.');
          }
        }
      } catch (checkErr) {
        if (checkErr.message === 'This email is already registered. Please sign in instead.') {
          throw checkErr;
        }
        // Server unreachable — check local registrations
        const localUsers = JSON.parse(localStorage.getItem('acquireos_local_users') || '[]');
        const existingLocal = localUsers.find(u => u.email.toLowerCase() === email.toLowerCase());
        if (existingLocal && existingLocal.status !== 'Rejected') {
          throw new Error('This email is already registered. Please sign in instead.');
        }
      }

      // Attempt server sync; fall back to localStorage if the server is unavailable
      const newUser = {
        id: 'u_' + Date.now(),
        name: name || email.split('@')[0],
        email,
        company: company || '',
        role,
        status: 'Pending', // Register with Pending status for admin approval flow
        registeredAt: new Date().toISOString(),
        userType,
        orgName,
        orgUrl,
        orgDetails,
        indStrategy,
        indDealSize,
        indAccredited
      };

      let serverSynced = false;
      try {
        const syncRes = await fetch('/api/admin/users', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newUser),
        });
        serverSynced = syncRes.ok;
      } catch (_) {
        // Network error — proceed with local-only registration
      }

      if (!serverSynced) {
        // Server unavailable: store as Active locally so the user can log in immediately
        const localUser = { ...newUser, status: 'Active' };
        const localUsers = JSON.parse(localStorage.getItem('acquireos_local_users') || '[]');
        localUsers.push(localUser);
        localStorage.setItem('acquireos_local_users', JSON.stringify(localUsers));
      }

      set({ isLoading: false });
      return { success: true };
    } catch (error) {
      set({ isLoading: false });
      return { success: false, error: error.message };
    }
  },

  verifyEmailCode: async (code) => {
    set({ isLoading: true });

    try {
      await new Promise(resolve => setTimeout(resolve, 500));

      const sentAt = get().verificationSentAt;

      if (!sentAt || Date.now() - sentAt > VERIFICATION_TTL_MS) {
        throw new Error('Verification code expired. Please request a new code.');
      }

      if (code === get().verificationCode) {
        const user = get().tempUser;

        // Fetch registered user profile from backend to get their correct name/company/role!
        try {
          const profileRes = await fetch(`/api/admin/users`);
          if (profileRes.ok) {
            const data = await profileRes.json();
            const dbUser = data.users?.find(u => u.email.toLowerCase() === user.email.toLowerCase());
            if (dbUser) {
              user.name = dbUser.name;
              user.role = dbUser.role;
              user.company = dbUser.company;
            }
          }
        } catch (e) {
          console.error("Failed to fetch verified user details:", e);
        }

        saveSession(user);

        // Sync verified active user profile to backend (ensure role and company matches DB)
        try {
          fetch('/api/admin/users', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: user.id,
              name: user.name,
              email: user.email,
              company: user.company || '',
              role: user.role,
              status: 'Active'
            })
          }).catch(err => console.error("Sync user error:", err));
        } catch (e) {
          console.error("Failed to fetch user sync:", e);
        }

        set({
          isAuthenticated: true,
          user,
          tempUser: null,
          verificationCode: null,
          verificationSentAt: null,
          lastEmailPreview: null,
          isLoading: false,
          isVerifying: false
        });

        return { success: true };
      } else {
        throw new Error('Invalid verification code');
      }
    } catch (error) {
      set({ isLoading: false });
      return { success: false, error: error.message };
    }
  },

  loginAsAdmin: async (email, password) => {
    set({ isLoading: true });
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      const adminPassword = import.meta.env.VITE_ADMIN_PASSWORD || 'admin123';
      if (email === 'admin@acquireos.com' && password === adminPassword) {
        const adminUser = {
          id: 'u_005',
          name: 'Admin User',
          email: 'admin@acquireos.com',
          company: 'AcquireOS Team',
          role: 'admin',
          subscription: 'pro',
          authProvider: 'email'
        };
        
        saveSession(adminUser);
        
        // Sync admin user to the backend
        try {
          await fetch('/api/admin/users', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(adminUser)
          });
        } catch (e) {
          console.error("Failed to sync admin user:", e);
        }

        set({
          isAuthenticated: true,
          user: adminUser,
          isLoading: false,
          isVerifying: false,
          verificationCode: null,
          verificationSentAt: null,
          lastEmailPreview: null,
          tempUser: null
        });
        return { success: true };
      } else {
        throw new Error('Invalid administrator email or password.');
      }
    } catch (error) {
      set({ isLoading: false });
      return { success: false, error: error.message };
    }
  },

  completeGoogleLogin: async (googleProfile) => {
    const email = googleProfile.email;
    try {
      const checkRes = await fetch(`/api/admin/users/check?email=${encodeURIComponent(email)}`);
      if (checkRes.ok) {
        const checkData = await checkRes.json();
        if (!checkData.exists) {
          throw new Error('This email is not registered. Please sign up first.');
        }
        if (checkData.status === 'Pending') {
          throw new Error('Your registration is pending administrator review. You will receive an email once approved.');
        }
        if (checkData.status === 'Rejected') {
          throw new Error('Your registration request has been rejected by the administrator.');
        }
        if (checkData.status === 'Suspended') {
          throw new Error('Your account access has been suspended. Please contact support.');
        }
      }

      const googleUser = {
        ...googleProfile,
        role: googleProfile.role || 'investor',
        subscription: 'pro',
        authProvider: 'google'
      };

      saveSession(googleUser);

      set({
        isAuthenticated: true,
        user: googleUser,
        isLoading: false,
        isVerifying: false,
        verificationCode: null,
        verificationSentAt: null,
        lastEmailPreview: null,
        tempUser: null
      });

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  completeMicrosoftLogin: async (msProfile) => {
    const email = msProfile.email;
    try {
      const checkRes = await fetch(`/api/admin/users/check?email=${encodeURIComponent(email)}`);
      if (checkRes.ok) {
        const checkData = await checkRes.json();
        if (!checkData.exists) {
          throw new Error('This email is not registered. Please sign up first.');
        }
        if (checkData.status === 'Pending') {
          throw new Error('Your registration is pending administrator review. You will receive an email once approved.');
        }
        if (checkData.status === 'Rejected') {
          throw new Error('Your registration request has been rejected by the administrator.');
        }
        if (checkData.status === 'Suspended') {
          throw new Error('Your account access has been suspended. Please contact support.');
        }
      }

      const msUser = {
        ...msProfile,
        role: msProfile.role || 'investor',
        subscription: 'pro',
        authProvider: 'outlook',
      };

      saveSession(msUser);

      set({
        isAuthenticated: true,
        user: msUser,
        isLoading: false,
        isVerifying: false,
        verificationCode: null,
        verificationSentAt: null,
        lastEmailPreview: null,
        tempUser: null,
      });

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  login: async (credentials) => {
    return get().loginWithEmail(credentials);
  },

  logout: () => {
    clearSession();
    set({
      isAuthenticated: false,
      user: null,
      isVerifying: false,
      verificationCode: null,
      verificationSentAt: null,
      lastEmailPreview: null,
      tempUser: null
    });
  },

  // Update user role (admin/host only)
  updateUserRole: (role) => {
    localStorage.setItem('aq_user_role', role);
    set((state) => {
      if (!state.user) return {};
      const updated = { ...state.user, role };
      saveSession(updated);
      return { user: updated };
    });
  },

  sendRegistrationOTP: async (regData) => {
    set({ isLoading: true });
    try {
      const { email, name } = regData;
      if (!email) {
        throw new Error('Email address is required');
      }

      // Check if user already exists
      try {
        const checkRes = await fetch(`/api/admin/users/check?email=${encodeURIComponent(email)}`);
        if (checkRes.ok) {
          const checkData = await checkRes.json();
          if (checkData.exists && checkData.status !== 'Rejected') {
            throw new Error('This email is already registered. Please sign in instead.');
          }
        }
      } catch (checkErr) {
        if (checkErr.message === 'This email is already registered. Please sign in instead.') {
          throw checkErr;
        }
        // Local check fallback
        const localUsers = JSON.parse(localStorage.getItem('acquireos_local_users') || '[]');
        const existingLocal = localUsers.find(u => u.email.toLowerCase() === email.toLowerCase());
        if (existingLocal && existingLocal.status !== 'Rejected') {
          throw new Error('This email is already registered. Please sign in instead.');
        }
      }

      const verificationCode = get().generateVerificationCode();
      const emailResult = await get().sendVerificationEmail(email, verificationCode, name);

      if (!emailResult.success) {
        throw new Error(emailResult.error || 'Failed to send verification email');
      }

      if (import.meta.env.DEV) {
        console.info(`%c[AcquireOS Dev] Signup OTP Code for ${email}: ${verificationCode}`, 'background:#047857;color:#fff;padding:4px 10px;border-radius:4px;font-size:14px;font-weight:bold;');
      }

      set({
        tempRegistration: regData,
        verificationCode,
        verificationSentAt: Date.now(),
        isVerifyingRegister: true,
        isLoading: false
      });

      return { success: true };
    } catch (error) {
      set({ isLoading: false });
      return { success: false, error: error.message };
    }
  },

  verifyRegistrationOTP: async (code) => {
    set({ isLoading: true });
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      const sentAt = get().verificationSentAt;

      if (!sentAt || Date.now() - sentAt > VERIFICATION_TTL_MS) {
        throw new Error('Verification code expired. Please request a new code.');
      }

      if (code !== get().verificationCode) {
        throw new Error('Invalid verification code');
      }

      // Code is correct! Save the user as Active (auto-approved)
      const regData = get().tempRegistration;
      if (!regData) {
        throw new Error('Registration session not found. Please try again.');
      }

      const newUser = {
        id: regData.id || 'u_' + Date.now(),
        name: regData.name,
        email: regData.email,
        company: regData.company || '',
        role: regData.role || 'investor',
        status: 'Pending', // Register with Pending status for admin approval flow
        registeredAt: new Date().toISOString(),
        userType: regData.userType || 'individual',
        orgName: regData.orgName || null,
        orgUrl: regData.orgUrl || null,
        orgDetails: regData.orgDetails || null,
        indStrategy: regData.indStrategy || null,
        indDealSize: regData.indDealSize || null,
        indAccredited: regData.indAccredited || null
      };

      let serverSynced = false;
      try {
        const syncRes = await fetch('/api/admin/users', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newUser),
        });
        serverSynced = syncRes.ok;
      } catch (_) {
        // Network error — proceed with local-only registration
      }

      if (!serverSynced) {
        // Server unavailable: store as Active locally so the user can log in immediately
        const localUser = { ...newUser, status: 'Active' };
        const localUsers = JSON.parse(localStorage.getItem('acquireos_local_users') || '[]');
        localUsers.push(localUser);
        localStorage.setItem('acquireos_local_users', JSON.stringify(localUsers));
      }

      set({
        tempRegistration: null,
        verificationCode: null,
        verificationSentAt: null,
        isVerifyingRegister: false,
        isLoading: false
      });

      return { success: true };
    } catch (error) {
      set({ isLoading: false });
      return { success: false, error: error.message };
    }
  },

  // Restores session from localStorage on app load
  checkAuth: () => {
    const session = loadSession();
    if (session?.user) {
      set({
        isAuthenticated: true,
        user: session.user,
        isLoading: false,
        isVerifying: false,
        verificationCode: null,
        verificationSentAt: null,
        lastEmailPreview: null,
        tempUser: null
      });
    } else {
      set({
        isAuthenticated: false,
        user: null,
        isLoading: false,
        isVerifying: false,
        verificationCode: null,
        verificationSentAt: null,
        lastEmailPreview: null,
        tempUser: null
      });
    }
  }
}));

export default useAuthStore;
